import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import NameInputBox from 'components/common/NameInputBox';
import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import { updateMyProfile } from 'api/userApi';

// Lean profile editor (personal-portal §4.6): plain form → submit → server validates. No live dedup.
const ProfileEditModal = ({ isOpen, contact, canEditEmail, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({ firstName: '', lastName: '', email: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && contact) {
      setValues({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
      });
      setErrors({});
    }
  }, [isOpen, contact]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async () => {
    const next = {};
    if (!values.firstName.trim()) next.firstName = 'First name is required';
    if (!values.lastName.trim()) next.lastName = 'Last name is required';
    if (canEditEmail) {
      if (!values.email.trim()) next.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(values.email))
        next.email = 'Email address is invalid';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSaving(true);
    try {
      const payload = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      };
      if (canEditEmail) payload.email = values.email.trim();
      await updateMyProfile(payload);
      toast.success('Profile updated.');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update your profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomNewConfirm
      isOpen={isOpen}
      title="Edit your details"
      confirmButtonText={saving ? 'Saving…' : 'Save'}
      confirmButtonVariant="primary"
      cancelButtonText="Cancel"
      isLoading={saving}
      onConfirm={submit}
      onClose={onClose}
    >
      <div>
        <NameInputBox
          label="Name"
          formValues={values}
          errors={errors}
          handleInputChange={handleChange}
          style={{ marginBottom: 16 }}
        />
        <CustomInput
          label="Email Address"
          name="email"
          type="email"
          value={values.email}
          error={errors.email}
          onChange={handleChange}
          disabled={!canEditEmail}
          style={{ marginBottom: 6 }}
        />
        {!canEditEmail && (
          <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0, marginBottom: 0 }}>
            Your email is managed by your club.
          </p>
        )}
      </div>
    </CustomNewConfirm>
  );
};

ProfileEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  contact: PropTypes.object,
  canEditEmail: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default ProfileEditModal;
